export * from './walkscore';
export * from './sale-info';
export * from './place-impact';
export * from './neighborhood-info';
export * from './code-enforcement';
export * from './enforcement-info';

const graphSet = (makeGraphs) => ({ overrides = {}, after } = {}) => {
  return makeGraphs({ after }).map(graph => overrides[graph.graph] || graph);
};

export const getDownloadColumns = ({ exclude = [], append = [] } = {}) => [
  'address',
  'all_sqft',
  'baths',
  'beds',
  'city',
  'count',
  'county',
  'delq_balance',
  'ed_ba_plus',
  'hh_w_kids',
  'is_vacant',
  'land_use',
  'med_hh_inc',
  'med_rent',
  'mortgage_foreclosure_1y',
  'mortgage_foreclosure_5y',
  'mortgage_foreclosure_hex',
  'mortgage_foreclosure',
  'occupied_1y',
  'occupied_5y',
  'occupied',
  'owner_name',
  'owner_name',
  'owner_occ_1y',
  'owner_occ_5y',
  'owner_occ',
  'owner_occupancy',
  'parcel_id',
  'pov_pct',
  'rc_2p',
  'rc_afam',
  'rc_asian',
  'rc_islnd',
  'rc_natv',
  'rc_other',
  'rc_white',
  'reoccupancy_detector',
  'res_sp_sqft_1y',
  'res_sp_sqft_5y',
  'res_sp_sqft',
  'residential_area',
  'structure',
  'tax_delinquent_1y',
  'tax_delinquent_5y',
  'tax_delinquent',
  'tax_foreclosure_1y',
  'tax_foreclosure_5y',
  'tax_foreclosure_hex',
  'tax_foreclosure',
  'tax_status',
  'total_balance',
  'total_owed',
  'total_paid',
  'vacancy_detector',
  'vacant_lot_1y',
  'vacant_lot_5y',
  'vacant_lot',
  'year_built',
  'zip',
  'zoning',
  'general_zoning',
]
  .filter((key) => !exclude.includes(key))
  .concat(append);

export const parcelDetails = [
  {
    column: 'geom',
    padding: 100,
    type: 'MAP',
    options: ['STREETVIEW', 'RADIUS'],
  },
  {
    type: 'ACTIONS',
    options: ['PRINT', 'DIRECTIONS', 'CSV', 'SHAPEFILE'],
    filterString: 'radius=radius%3A300',
    props: getDownloadColumns(),
  },
  {
    label: 'About the Parcel',
    type: 'TITLE',
  },
  {
    column: 'parcel_id',
    label: 'Parcel Id',
    type: 'TEXT',
  },
  {
    column: 'address',
    label: 'Address',
    type: 'TEXT',
  },
  {
    column: 'city',
    label: 'City',
    type: 'TEXT',
  },
  {
    column: 'zip',
    label: 'Zip Code',
    type: 'TEXT',
  },
  {
    column: 'county',
    label: 'County',
    type: 'TEXT',
  },
  {
    column: 'structure',
    label: 'Has Structure',
    type: 'TEXT',
  },
  {
    column: 'residential_area',
    label: 'Residential Sq. Feet',
    type: 'TEXT',
  },
  {
    column: 'beds',
    label: 'Bedrooms',
    type: 'TEXT',
  },
  {
    column: 'baths',
    label: 'Bathrooms',
    type: 'TEXT',
  },
  {
    column: 'year_built',
    label: 'Year Built',
    type: 'TEXT',
  },
  {
    column: 'general_zoning',
    label: 'General Zoning',
    type: 'TEXT',
  },
  {
    column: 'zoning',
    label: 'Zoning',
    type: 'TEXT',
  },
  {
    column: 'land_use',
    label: 'Current Use',
    type: 'TEXT',
  },
];

export const occupancyInformation = [
  {
    column: 'is_vacant',
    label: 'Occupied',
    type: 'TEXT_MAP',
    map: {
      0: 'Occupied',
      1: 'Unoccupied',
    },
  },
  {
    column: 'reoccupancy_detector',
    label: 'Re-Occupied',
    type: 'TEXT',
  },
  {
    column: 'vacancy_detector',
    label: 'Vacated',
    type: 'TEXT',
  },
  {
    column: 'owner_name',
    label: 'Owner',
    type: 'TEXT',
  },
  {
    column: 'owner_address_full',
    label: 'Owner Address',
    type: 'TEXT',
  },
  // {
  //   column: 'owner_since',
  //   label: 'Owner Since',
  //   type: 'TEXT',
  // },
  {
    column: 'owner_prop_count',
    label: 'Other properties owned across the city',
    type: 'TEXT',
  },
  {
    column: 'owner_occupancy',
    label: 'Owner Occupied',
    type: 'TEXT_MAP',
    map: {
      1: 'Owner Occupied',
      0: 'Not Owner Occupied',
    },
  },
];

export const impact = [
  {
    label: 'Neighborhood Revitalization Impact on Home Values within 500 Feet',
    type: 'TITLE',
  },
  {
    column: 'rehab_impact_500',
    default: 'N/A',
    source: 'predicted_impact',
    label: 'Predicted Rehabilitation Impact',
    type: 'DOLLARS',
  },
  {
    column: 'date',
    source: 'rehab_impact',
    label: 'Rehabilitation Date',
    type: 'TEXT',
  },
  {
    column: 'rehab_impact',
    default: 'N/A',
    source: 'rehab_impact',
    label: 'Rehabilitation Impact',
    type: 'DOLLARS',
  },
  {
    column: 'rb_occ500',
    source: 'rehab_impact',
    label: 'Number Of Parcels That Impact Spread Across',
    type: 'TEXT',
  },
  {
    column: 'impact_per',
    source: 'rehab_impact',
    label: 'Impact per affected property',
    type: 'DOLLARS',
  },
  {
    column: 'demo_impact_500',
    default: 'N/A',
    source: 'predicted_impact',
    label: 'Predicted Demolition Impact',
    type: 'DOLLARS',
  },
  {
    column: 'date',
    source: 'demo_impact',
    label: 'Demolition Date',
    type: 'TEXT',
  },
  {
    column: 'demo_impact',
    default: 'N/A',
    source: 'demo_impact',
    label: 'Demolition Impact',
    type: 'DOLLARS',
  },
  {
    column: 'rb_occ500',
    source: 'demo_impact',
    label: 'Number Of Parcels That Impact Spread Across',
    type: 'TEXT',
  },
  {
    column: 'impact_per',
    source: 'demo_impact',
    label: 'Impact per affected property',
    type: 'DOLLARS',
  },
];


export const occupancyGraphs = graphSet(({ after } = {}) => [
  {
    label: 'Occupancy Indicators',
    type: 'TITLE',
    newPage: true,
    excludeFromLimited: true,
  },
  {
    label: 'Occupancy',
    graph: 'occupancy_parcels_single',
    type: 'LINE_GRAPH',
    after,
    exclude: ['Unoccupied', 'undefined'],
    map: {
      0: 'Occupied',
      1: 'Unoccupied',
    },
    compare: [
      {
        to: 'CITY',
        graph: 'occupancy_parcels',
        label: 'Occupancy for the city',
      },
    ],
    options: {
      showPieChart: true,
    },
  },
  {
    label: 'Owner Occupancy',
    graph: 'owner_occupancy_parcels_single',
    type: 'LINE_GRAPH',
    after,
    exclude: ['Not Owner Occupied', 'undefined'],
    map: {
      1: 'Owner Occupied',
      0: 'Not Owner Occupied',
    },
    compare: [
      {
        to: 'CITY',
        graph: 'owner_occupancy_parcels',
        label: 'Owner Occupancy for the city',
      },
    ],
  },
]);

export const occupancyGraphsCounty = graphSet(({ after } = {}) => [
  {
    label: 'Occupancy Indicators',
    type: 'TITLE',
    newPage: true,
    excludeFromLimited: true,
  },
  {
    label: 'Occupancy',
    graph: 'occupancy_parcels_single',
    type: 'LINE_GRAPH',
    after,
    exclude: ['Unoccupied', 'undefined'],
    map: {
      0: 'Occupied',
      1: 'Unoccupied',
    },
    compare: [
      {
        to: 'CITY',
        graph: 'occupancy_parcels',
        label: 'Occupancy for the city',
      },
    ],
    options: {
      showPieChart: true,
    },
  },
  {
    label: 'Owner Occupancy',
    graph: 'owner_occupancy_parcels_single',
    type: 'LINE_GRAPH',
    after,
    exclude: ['Not Owner Occupied', 'undefined'],
    map: {
      1: 'Owner Occupied',
      0: 'Not Owner Occupied',
    },
    compare: [
      {
        to: 'CITY',
        graph: 'owner_occupancy_parcels',
        label: 'Owner Occupancy for the city',
      },
    ],
  },
]);

export const taxStatusMI = [
  {
    column: 'tax_status',
    label: 'Tax Status',
    type: 'TEXT_MAP',
    map: {
      'Tax Current': 'Tax Current',
      'Tax Exempt': 'Tax Exempt',
      'Tax Delinquent': 'Tax Delinquent',
      'Tax Foreclosable': 'Tax Forfeiture',
    },
  },
];

export const codeEnforcementJackson = [
  {
    label: 'Water Use',
    type: 'TITLE',
    excludeFromLimited: true,
  },
  {
    title: 'Water Use',
    graph: 'water_use',
    type: 'LINE_GRAPH',
    useRatio: false,
    after: 2012,
    unit: 'Gallons',
    hideLegend: true,
  },
  {
    title: 'Changeability',
    column: 'changeability',
    type: 'METER',
    total: 5,
  },

  {
    label: 'Licenses and permits',
    type: 'TITLE',
  },
  {
    column: 'rental_license',
    label: 'Rental Permit or Certificate',
    type: 'TEXT',
  },
  {
    column: 'owner_occupancy',
    label: 'PRE',
    type: 'TEXT_MAP',
    map: {
      0: 'No',
      1: 'Yes',
    },
  },
  {
    label: 'LARA License (where applicable)',
    type: 'LARA_LINK',
  },
  {
    column: 'open_permit_count',
    label: 'Open permits count',
    type: 'TEXT',
  },
  {
    label: 'Violations, Enforcements and Calls for service',
    type: 'TITLE',
  },
  {
    column: 'crime_incident',
    label: 'Crime and recent calls for service',
    default: '0',
    type: 'TEXT',
  },
  {
    label: 'Crime Mapping',
    value:
      'https://jacksonmigis.maps.arcgis.com/apps/opsdashboard/index.html#/c1770bc3eb774e19a5497c39652cf736',
    type: 'LINK',
  },
  {
    column: 'open_violation_count',
    label: 'Open violations (Enforcement count)',
    default: '0',
    type: 'TEXT',
  },
  {
    column: 'violation_2y_count',
    label:
      'Violations since last change of ownership at this property or in the past two years',
    default: '0',
    type: 'TEXT',
  },
  {
    column: 'violation_owner_2y',
    label:
      'Violations at other properties owned by owner in past 2 years or since ownership',
    default: '0',
    type: 'TEXT',
  },
];

export const codeEnforcementReport = [
  {
    label: 'Water Use',
    type: 'TITLE',
    excludeFromLimited: true,
  },
  {
    title: 'Water Use',
    graph: 'water_use',
    type: 'LINE_GRAPH',
    useRatio: false,
    after: 2012,
    unit: 'Gallons',
    hideLegend: true,
  },
  {
    label: 'Licenses and permits',
    type: 'TITLE',
  },
  {
    column: 'rental_license',
    label: 'Rental Permit or Certificate',
    type: 'TEXT',
  },
  {
    column: 'owner_occupancy',
    label: 'PRE',
    type: 'TEXT_MAP',
    map: {
      0: 'No',
      1: 'Yes',
    },
  },
  {
    label: 'LARA License (where applicable)',
    type: 'LARA_LINK',
  },
  {
    column: 'open_permit_count',
    label: 'Open permits count',
    type: 'TEXT',
  },
  {
    label: 'Violations, Enforcements and Calls for service',
    type: 'TITLE',
  },
  {
    column: 'crime_incident',
    label: 'Crime and recent calls for service within 500ft in the past two years',
    default: '0',
    type: 'TEXT',
  },
  {
    column: 'open_violation_count',
    label: 'Open violations (Enforcement count)',
    default: '0',
    type: 'TEXT',
  },
  {
    column: 'open_violation_type',
    label: 'Open Violation Types',
    type: 'TEXT',
  },
  {
    column: 'violation_2y_count',
    label:
      'Violations since last change of ownership at this property or in the past two years',
    default: '0',
    type: 'TEXT',
  },
  {
    column: 'violation_owner_2y',
    label:
      'Violations at other properties owned by owner in past 2 years or since ownership',
    default: '0',
    type: 'TEXT',
  },
];

export const taxStatus = [
  {
    column: 'tax_status',
    label: 'Tax Status',
    type: 'TEXT',
  },
];

export const structureGraphs = ({ after } = {}) => [
  {
    label: 'Has Structure(s)',
    type: 'TITLE',
    excludeFromLimited: true,
  },
  {
    label: 'Has Structure',
    graph: 'structures_single',
    exclude: ['No'],
    map: {
      Yes: 'Has Structures',
    },
    type: 'LINE_GRAPH',
    after,
    compare: [
      {
        to: 'CITY',
        graph: 'structures',
        label: 'No Structure for the city',
      },
    ],
  },
];

export const taxInformationMi = ({ exclude = [], after } = {}) => [
  {
    label: 'Tax Information',
    type: 'TITLE',
    newPage: true,
    excludeFromLimited: true,
  },
  {
    column: 'total_owed',
    label: 'Taxes Owed',
    type: 'DOLLARS',
    default: 'N/A',
  },
  {
    column: 'total_paid',
    label: 'Taxes Paid',
    type: 'DOLLARS',
  },
  {
    column: 'total_balance',
    label: 'Remaining Tax Balance',
    type: 'DOLLARS',
  },
  {
    column: 'delq_balance',
    label: 'Delinquent Tax Balance',
    type: 'DOLLARS',
  },
  {
    label: 'Tax Graphs',
    type: 'TITLE',
    newPage: true,
    excludeFromLimited: true,
  },
  {
    label: 'Tax Status',
    exclude: ['Unknown'],
    graph: 'tax_stat_single_parcels',
    type: 'LINE_GRAPH',
    after,
    compare: [
      {
        to: 'CITY',
        graph: 'tax_status_parcels',
        label: 'No Structure for the city',
      },
    ],
    options: {
      showPieChart: true,
    },
    map: {
      'Tax Current': 'Tax Current',
      'Tax Exempt': 'Tax Exempt',
      'Tax Delinquent': 'Tax Delinquent',
      'Tax Foreclosable': 'Tax Forfeiture',
    },
  },
].filter(({ column, label }) => !exclude.includes(column) && !exclude.includes(label));

export const taxInformation = ({ after } = {}) => [
  {
    label: 'Tax Information',
    type: 'TITLE',
    newPage: true,
  },
  {
    column: 'total_owed',
    label: 'Taxes Owed',
    type: 'DOLLARS',
    default: 'N/A',
  },
  {
    column: 'total_paid',
    label: 'Taxes Paid',
    type: 'DOLLARS',
  },
  {
    column: 'total_balance',
    label: 'Remaining Tax Balance',
    type: 'DOLLARS',
  },
  {
    column: 'delq_balance',
    label: 'Delinquent Tax Balance',
    type: 'DOLLARS',
  },
  {
    label: 'Tax Graphs',
    type: 'TITLE',
    newPage: true,
    excludeFromLimited: true,
  },
  {
    label: 'Tax Status',
    exclude: ['Unknown'],
    graph: 'tax_stat_single_parcels',
    type: 'LINE_GRAPH',
    after,
    compare: [
      {
        to: 'CITY',
        graph: 'tax_status_parcels',
        label: 'No Structure for the city',
      },
    ],
    options: {
      showPieChart: true,
    },
  },
];

export const foreclosureGraphs = ({ after } = {}) => [
  {
    label: 'Foreclosures',
    type: 'TITLE',
    newPage: true,
    excludeFromLimited: true,
  },
  {
    label: 'Tax Foreclosures',
    exclude: ['Not Foreclosed'],
    graph: 'tax_foreclosures_parcels_single',
    type: 'LINE_GRAPH',
    after,
    compare: [
      {
        to: 'CITY',
        graph: 'tax_foreclosures_parcels',
        label: 'Tax Foreclosures for the city',
      },
    ],
  },
  {
    label: 'Mortgage Foreclosures',
    exclude: ['Not Foreclosed'],
    graph: 'mortgage_foreclosures_parcels_single',
    type: 'LINE_GRAPH',
    after,
    compare: [
      {
        to: 'CITY',
        graph: 'mortgage_foreclosures_parcels',
        label: 'Mortgage Foreclosures for the city',
      },
    ],
  },
];

export const zoningGraphs = [
  {
    label: 'Other Indicators',
    type: 'TITLE',
    excludeFromLimited: true,
  },
  {
    label: 'General Zoning',
    graph: 'general_zoning_single_parcels',
    type: 'PIE_GRAPH',
    compare: [
      {
        to: 'CITY',
        graph: 'general_zoning_parcels',
        label: 'Zoning for the city',
      },
    ],
  },
  {
    label: 'Zoning',
    graph: 'zoning_single_parcels',
    type: 'PIE_GRAPH',
    compare: [
      {
        to: 'CITY',
        graph: 'zoning_parcels',
        label: 'Zoning for the city',
      },
    ],
  },
];

export const census = [
  {
    label: 'Socio-economics & Demographics (within census block group)',
    type: 'TITLE',
  },
  {
    label: 'Census Comparison',
    type: 'CENSUS_COMPARISON_TABLE',
    table: 'census_table',
  },
];

export const censusCounty = [
  {
    label: 'Socio-economics & Demographics (within census block group)',
    type: 'TITLE',
  },
  {
    label: 'Census Comparison',
    type: 'CENSUS_COMPARISON_TABLE',
    table: 'county_census_table',
  },
];

export const heatmap = [
  {
    label: 'Real Estate & Property Conditions and Trends (within 500 feet)',
    type: 'TITLE',
  },
  {
    label: 'Area Residential Price Per Sq. Foot',
    column: 'res_sp_sqft',
    type: 'DOLLARS',
  },
  {
    label: 'Area Building Density (sq. ft. per hexagon)',
    column: 'all_sqft',
    type: 'TEXT',
  },
  {
    label: 'Area Density (parcels per hexagon)',
    column: 'count',
    type: 'TEXT',
  },
  {
    label: 'Area Percent Vacant Lots',
    column: 'vacant_lot',
    type: 'PERCENT',
  },
  {
    label: 'Area Percent Tax Delinquent',
    column: 'tax_delinquent',
    type: 'PERCENT',
  },
  {
    label: 'Area Percent Tax Foreclosure',
    column: 'tax_foreclosure_hex',
    type: 'PERCENT',
  },
  {
    label: 'Area Percent Mortgage Foreclosure',
    column: 'mortgage_foreclosure_hex',
    type: 'PERCENT',
  },
  {
    label: 'Area Percent Owner Occupied',
    column: 'owner_occ',
    type: 'PERCENT',
  },
  {
    label: 'Area Percent Occupied',
    column: 'occupied',
    type: 'PERCENT',
  },
  {
    label: 'Area Change in Residential Price Per Sq. Foot - 1 year',
    column: 'res_sp_sqft_1y',
    type: 'PERCENT',
  },
  {
    label: 'Area Change in Residential Price Per Sq. Foot - 5 year',
    column: 'res_sp_sqft_5y',
    type: 'PERCENT',
  },
  {
    label: 'Area Change in Tax Delinquency - 1 year',
    column: 'tax_delinquent_1y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Tax Delinquency - 5 year',
    column: 'tax_delinquent_5y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Tax Foreclosure - 1 year',
    column: 'tax_foreclosure_1y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Tax Foreclosure - 5 year',
    column: 'tax_foreclosure_5y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Mortgage Foreclosure - 1 year',
    column: 'mortgage_foreclosure_1y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Mortgage Foreclosure - 5 year',
    column: 'mortgage_foreclosure_5y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Occupancy - 1 year',
    column: 'occupied_1y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Occupancy - 5 year',
    column: 'occupied_5y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Owner Occupancy - 1 year',
    column: 'owner_occ_1y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Owner Occupancy - 5 year',
    column: 'owner_occ_5y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Vacant Lot - 1 year',
    column: 'vacant_lot_1y',
    type: 'TEXT',
  },
  {
    label: 'Area Change in Vacant Lot - 5 year',
    column: 'vacant_lot_5y',
    type: 'TEXT',
  },
];
